import React, { useEffect, useRef, useState } from 'react';
import { Download, ExternalLink, Printer, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import api, { textoErrorApi } from '../api/api';

// Visor de documentos reutilizable (PDF o imagen) en un modal casi a pantalla
// completa. Dos orígenes:
// - `url`: fichero del servidor (p. ej. /api/uploads/...). Se descarga como
//   blob con la sesión (Bearer) y se muestra en un iframe (PDF) o una imagen.
// - `driveId`: fichero de Google Drive, con su vista previa en un iframe.
// - `contenidoBase64`: fichero que ya está en el navegador (p. ej. un adjunto
//   del editor de correo que aún no se ha enviado). Se muestra sin pedir nada
//   al servidor; el tipo sale de `mime` o del nombre (`titulo`).
// Si llegan los dos primeros, manda el fichero del servidor (siempre se puede
// abrir con la sesión) y Drive queda como botón "Abrir en Drive".

// Id de Drive a partir de un enlace (…/file/d/<id>/… o ?id=<id>)
export const driveIdDeUrl = (url) => {
  if (!url || !/drive\.google\.com|docs\.google\.com/i.test(url)) return null;
  const m = String(url).match(/\/d\/([a-zA-Z0-9_-]{10,})/) || String(url).match(/[?&]id=([a-zA-Z0-9_-]{10,})/);
  return m ? m[1] : null;
};

const MIME_POR_EXTENSION = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
};

const mimeDeNombre = (nombre) => {
  const ext = String(nombre || '').split('?')[0].split('.').pop().toLowerCase();
  return MIME_POR_EXTENSION[ext] || '';
};

const nombreDeUrl = (url) => {
  const limpio = String(url || '').split('?')[0];
  return decodeURIComponent(limpio.split('/').pop() || 'documento');
};

// Ruta relativa a la base del cliente `api` (que ya termina en /api)
const rutaParaApi = (url) => (url.startsWith('/api/') ? url.slice(4) : url);

// El cuerpo de un error con responseType blob llega como Blob: se lee el detail
const errorDeBlob = async (e) => {
  try {
    const data = e?.response?.data;
    if (data instanceof Blob) {
      const json = JSON.parse(await data.text());
      return textoErrorApi(json?.detail, '');
    }
    return textoErrorApi(data?.detail, '');
  } catch (_) {
    return '';
  }
};

// Móvil: pantalla estrecha o dedo como puntero. En iOS/Android un PDF dentro de
// un iframe no se puede leer (solo la primera página o nada), así que se abre aparte.
const detectarMovil = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.innerWidth < 768 || !!window.matchMedia?.('(pointer: coarse)')?.matches;
  } catch (_) {
    return window.innerWidth < 768;
  }
};

const VisorDocumento = ({ abierto, onCerrar, titulo, url, driveId, mime, contenidoBase64 }) => {
  const [esMovil] = useState(detectarMovil);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [blobUrl, setBlobUrl] = useState('');
  const [tipo, setTipo] = useState('');
  const iframeRef = useRef(null);

  const esExterna = !!url && /^https?:\/\//i.test(url);
  const driveFinal = driveId || (esExterna ? driveIdDeUrl(url) : null);
  const usarLocal = !!contenidoBase64;
  // `usarServidor` cubre también el fichero local: los dos se enseñan igual (blob)
  const usarServidor = usarLocal || (!!url && !esExterna);
  const nombreDescarga = titulo || nombreDeUrl(url);

  useEffect(() => {
    setError('');
    if (!abierto || !usarServidor) return undefined;
    let vivo = true;
    let creada = '';
    if (usarLocal) {
      try {
        const binario = window.atob(String(contenidoBase64).replace(/^data:[^,]*,/, ''));
        const bytes = new Uint8Array(binario.length);
        for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
        const tipoFinal = mime || mimeDeNombre(titulo) || 'application/octet-stream';
        creada = window.URL.createObjectURL(new Blob([bytes], { type: tipoFinal }));
        setTipo(tipoFinal);
        setBlobUrl(creada);
        setCargando(false);
      } catch (e) {
        setError('No se ha podido abrir este adjunto.');
      }
      return () => {
        if (creada) window.URL.revokeObjectURL(creada);
      };
    }
    setCargando(true);
    setError('');
    setBlobUrl('');
    api.get(rutaParaApi(url), { responseType: 'blob' })
      .then((res) => {
        if (!vivo) return;
        const tipoRespuesta = (res.headers?.['content-type'] || '').split(';')[0].trim();
        const tipoFinal = (tipoRespuesta && tipoRespuesta !== 'application/octet-stream')
          ? tipoRespuesta
          : (mime || mimeDeNombre(url) || res.data?.type || 'application/pdf');
        const blob = new Blob([res.data], { type: tipoFinal });
        creada = window.URL.createObjectURL(blob);
        setTipo(tipoFinal);
        setBlobUrl(creada);
      })
      .catch(async (e) => {
        if (!vivo) return;
        const estado = e?.response?.status;
        const detalle = await errorDeBlob(e);
        if (!vivo) return;
        if (detalle) setError(detalle);
        else if (estado === 404) setError('El documento no está en el servidor.');
        else if (estado === 401 || estado === 403) setError('No tienes permiso para ver este documento.');
        else setError('No se ha podido cargar el documento. Inténtalo de nuevo.');
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => {
      vivo = false;
      if (creada) window.URL.revokeObjectURL(creada);
    };
  }, [abierto, usarServidor, usarLocal, contenidoBase64, titulo, url, mime]);

  const esImagen = tipo.startsWith('image/');

  const imprimir = () => {
    if (!blobUrl) return;
    if (!esImagen) {
      try {
        iframeRef.current?.contentWindow?.focus();
        iframeRef.current?.contentWindow?.print();
      } catch (e) {
        setError('No se ha podido imprimir desde aquí. Descárgalo e imprímelo.');
      }
      return;
    }
    // Imagen: una ventana con solo la imagen y su diálogo de impresión
    const ventana = window.open('', '_blank');
    if (!ventana) {
      setError('El navegador ha bloqueado la ventana de impresión. Descárgalo e imprímelo.');
      return;
    }
    const doc = ventana.document;
    doc.title = nombreDescarga;
    doc.body.style.margin = '0';
    const img = doc.createElement('img');
    img.style.maxWidth = '100%';
    img.style.display = 'block';
    img.style.margin = '0 auto';
    img.onload = () => { ventana.focus(); ventana.print(); };
    img.src = blobUrl;
    doc.body.appendChild(img);
  };

  // En móvil la barra son iconos de 40 px (con aria-label); en escritorio, icono y texto
  const claseBoton = esMovil
    ? 'inline-flex items-center justify-center w-10 h-10 rounded-lg border text-sm transition'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition';
  const claseBotonGrande = 'inline-flex items-center justify-center gap-2 w-full max-w-xs px-4 py-3 rounded-lg border text-base font-medium transition';

  return (
    <Dialog open={!!abierto} onOpenChange={(abrir) => { if (!abrir) onCerrar(); }}>
      <DialogContent
        className={`bg-white p-0 gap-0 flex flex-col overflow-hidden [&>button:last-child]:hidden ${
          esMovil ? 'h-[100dvh] w-screen max-w-none rounded-none border-0' : 'w-[96vw] max-w-[96vw] h-[92dvh]'
        }`}
        data-testid="visor-documento"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b bg-white">
          <DialogTitle className="text-sm font-semibold text-gray-900 truncate min-w-0 flex-1">
            {titulo || 'Documento'}
          </DialogTitle>
          <DialogDescription className="sr-only">Vista previa del documento</DialogDescription>
          <div className="flex flex-wrap items-center gap-2">
            {usarServidor && blobUrl && (
              <>
                <a
                  href={blobUrl}
                  download={nombreDescarga}
                  className={`${claseBoton} border-duetto-primary text-duetto-primary hover:bg-duetto-primary hover:text-white`}
                  data-testid="visor-descargar"
                  aria-label="Descargar"
                  title="Descargar"
                >
                  <Download className={esMovil ? 'w-5 h-5' : 'w-4 h-4'} />{!esMovil && ' Descargar'}
                </a>
                {!esMovil && (
                  <button
                    type="button"
                    onClick={imprimir}
                    className={`${claseBoton} border-gray-300 text-gray-700 hover:bg-gray-50`}
                    data-testid="visor-imprimir"
                    aria-label="Imprimir"
                  >
                    <Printer className="w-4 h-4" /> Imprimir
                  </button>
                )}
              </>
            )}
            {driveFinal && (
              <a
                href={`https://drive.google.com/file/d/${driveFinal}/view`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${claseBoton} border-gray-300 text-gray-700 hover:bg-gray-50`}
                data-testid="visor-abrir-drive"
                aria-label="Abrir en Drive"
                title="Abrir en Drive"
              >
                <ExternalLink className={esMovil ? 'w-5 h-5' : 'w-4 h-4'} />{!esMovil && ' Abrir en Drive'}
              </a>
            )}
            <button
              type="button"
              onClick={onCerrar}
              className={`${claseBoton} border-gray-300 text-gray-700 hover:bg-gray-50`}
              data-testid="visor-cerrar"
              aria-label="Cerrar"
              title="Cerrar"
            >
              <X className={esMovil ? 'w-5 h-5' : 'w-4 h-4'} />{!esMovil && ' Cerrar'}
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-gray-100">
          {usarServidor && cargando && (
            <div className="h-full flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Cargando documento...
            </div>
          )}
          {error && (
            <div className="p-6">
              <div className="max-w-lg mx-auto bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm" role="alert">
                {error}
              </div>
            </div>
          )}
          {usarServidor && !cargando && blobUrl && (
            esImagen ? (
              <div className="h-full flex items-center justify-center p-4 overflow-auto">
                <img src={blobUrl} alt={titulo || 'Documento'} className="max-h-full max-w-full object-contain bg-white" />
              </div>
            ) : esMovil ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="visor-pdf-movil">
                <p className="text-sm text-gray-600 break-all">{nombreDescarga}</p>
                <a
                  href={blobUrl}
                  target="_blank"
                  rel="noopener"
                  className={`${claseBotonGrande} border-duetto-primary bg-duetto-primary text-white`}
                  data-testid="visor-abrir-movil"
                >
                  <ExternalLink className="w-5 h-5" /> Abrir
                </a>
                <a
                  href={blobUrl}
                  download={nombreDescarga}
                  className={`${claseBotonGrande} border-duetto-primary text-duetto-primary bg-white`}
                  data-testid="visor-descargar-movil"
                >
                  <Download className="w-5 h-5" /> Descargar
                </a>
              </div>
            ) : (
              <iframe ref={iframeRef} src={blobUrl} title={titulo || 'Documento'} className="w-full h-full bg-white border-0" />
            )
          )}
          {!usarServidor && driveFinal && (
            <iframe
              src={`https://drive.google.com/file/d/${driveFinal}/preview`}
              title={titulo || 'Documento'}
              className="w-full h-full bg-white border-0"
              allow="autoplay"
            />
          )}
          {!usarServidor && !driveFinal && esExterna && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-gray-600 p-6 text-center">
              <p>Este documento está en otra web y no se puede mostrar aquí.</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-duetto-primary hover:underline">
                Abrir en una pestaña
              </a>
            </div>
          )}
          {!url && !driveFinal && (
            <div className="h-full flex items-center justify-center text-sm text-gray-500">No hay documento que mostrar.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisorDocumento;
